import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Patient Info', to: '/patient-info' },
  { label: 'Refer a Patient', to: '/refer' },
  { label: 'Contact', to: '/contact' },
];

export function PublicNavBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navBg = scrolled ? 'var(--color-cream)' : 'var(--color-navy)';
  const textColor = scrolled ? 'var(--color-navy)' : 'rgba(255,255,255,0.9)';
  const textHover = scrolled ? 'var(--color-primary)' : 'var(--color-gold)';
  const logoSrc = scrolled ? '/images/logo-colour.png' : '/images/logo-white.png';

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: navBg,
        boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={logoSrc}
              alt="DrIKhan Gynae"
              className="h-[85px] py-1 transition-all duration-300"
            />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center space-x-5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="font-heading italic text-sm border-b border-transparent pb-0.5 transition-colors"
                style={{
                  fontFamily: 'var(--font-heading)',
                  color: textColor,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = textHover;
                  (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = textHover;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.color = textColor;
                  (e.currentTarget as HTMLAnchorElement).style.borderBottomColor = 'transparent';
                }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/login"
              className="px-4 py-2 rounded-md font-body font-medium text-sm border transition-colors"
              style={{
                borderColor: scrolled ? 'var(--color-primary)' : 'var(--color-gold)',
                color: scrolled ? 'var(--color-primary)' : 'var(--color-gold)',
                fontFamily: 'var(--font-body)',
              }}
              onMouseEnter={(e) => {
                const bg = scrolled ? 'var(--color-primary)' : 'var(--color-gold)';
                const fg = scrolled ? '#fff' : 'var(--color-navy)';
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = bg;
                (e.currentTarget as HTMLAnchorElement).style.color = fg;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLAnchorElement).style.color = scrolled ? 'var(--color-primary)' : 'var(--color-gold)';
              }}
            >
              Portal Login
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-md transition-colors"
            style={{ color: scrolled ? 'var(--color-navy)' : '#fff' }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ backgroundColor: scrolled ? 'var(--color-cream)' : 'var(--color-navy)' }}
            className="md:hidden border-t border-white/20 overflow-hidden"
          >
            <div className="px-4 py-3 space-y-3">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block font-heading italic transition-colors py-1"
                  style={{ fontFamily: 'var(--font-heading)', color: textColor }}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/login"
                className="block text-center px-4 py-2 rounded-md font-body font-medium text-sm border"
                style={{
                  borderColor: scrolled ? 'var(--color-primary)' : 'var(--color-gold)',
                  color: scrolled ? 'var(--color-primary)' : 'var(--color-gold)',
                  fontFamily: 'var(--font-body)',
                }}
                onClick={() => setMenuOpen(false)}
              >
                Portal Login
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
